import type { CampaignType, List, Platform, Prices, Talent } from "@/shared/types"
import { orderedTalentIds } from "@/utils/orderedTalentIds"

export type SortOption = {
    name: string
    platform: "tt" | "ig" | "yt" | "all"
    function: string
    value: string
    direction: "desc" | "asc"
}

const SORT_OPTION_BASE: Partial<SortOption>[] = [
    { platform: "ig", name: "Followers", function: "followers" },
    { platform: "ig", name: "Song Price", function: "songPriceInstagram" },
    { platform: "ig", name: "Reel Price", function: "brandPriceInstagramReel" },
    { platform: "tt", name: "Avg Views", function: "views" },
    { platform: "tt", name: "Price", function: "brandPriceTikTok" },
    { platform: "tt", name: "Price", function: "songPriceTikTok" },
    { platform: "tt", name: "Followers", function: "followers" },
    { platform: "tt", name: "Engagement", function: "engagement" },
    { platform: "yt", name: "Subscribers", function: "followers" },
    { platform: "yt", name: "Song Price", function: "songPriceYouTube" },
    { platform: "yt", name: "Integration Price", function: "brandPriceYouTubeIntegration" },
    { platform: "all", name: "Tag Relevance", function: "tagRelevance" },
    { platform: "all", name: "Selected Creators", function: "selectedCreators", direction: "desc" },
    { platform: "all", name: "List Order", function: "listOrder", direction: "asc" },
]

export const sortOptions: SortOption[] = SORT_OPTION_BASE.reduce((acc, option) => {
    if (option.direction) {
        acc.push({ ...option, value: `${option.platform}-${option.function}-${option.direction}` } as SortOption)
        return acc
    }
    acc.push({ ...option, direction: "desc", value: `${option.platform}-${option.function}-desc` } as SortOption)
    acc.push({ ...option, direction: "asc", value: `${option.platform}-${option.function}-asc` } as SortOption)
    return acc
}, [] as SortOption[])

const PLATFORM_SORT_PREFIX: Record<Platform, SortOption["platform"]> = {
    TikTok: "tt",
    Instagram: "ig",
    YouTube: "yt",
}

const DEFAULT_SORT_BY_PLATFORM: Record<Platform, string> = {
    TikTok: "tt-views-desc",
    Instagram: "ig-followers-desc",
    YouTube: "yt-followers-desc",
}

export function defaultSortForPlatforms(platforms: Platform[]): string {
    const first = platforms[0]
    if (first && DEFAULT_SORT_BY_PLATFORM[first]) {
        return DEFAULT_SORT_BY_PLATFORM[first]
    }
    return DEFAULT_SORT_BY_PLATFORM.TikTok
}

export function sortOptionsForPlatforms(
    platforms: Platform[],
    campaignType: CampaignType,
    isCustomList: boolean,
): SortOption[] {
    const prefixes = new Set(platforms.map((platform) => PLATFORM_SORT_PREFIX[platform]))
    return sortOptions.filter((option) => {
        if (!isCustomList && option.function === "listOrder") {
            return false
        }
        if (campaignType === "Song" && option.value.toLowerCase().includes("brand")) {
            return false
        }
        if (campaignType !== "Song" && option.value.toLowerCase().includes("song")) {
            return false
        }
        return option.platform === "all" || prefixes.has(option.platform)
    })
}

const SORT_PLATFORM: Record<SortOption["platform"], Platform | undefined> = {
    tt: "TikTok",
    ig: "Instagram",
    yt: "YouTube",
    all: undefined,
}

export function sortOptionPlatform(option: SortOption): Platform | undefined {
    return SORT_PLATFORM[option.platform]
}

export function sortTalent(
    talent: Talent[],
    sortBy: string,
    contentTags: string[],
    selectedIds: Set<string>,
    customList?: List,
): Talent[] {
    const sort = sortOptions.find((option) => option.value === sortBy)
    if (!sort) {
        return talent
    }

    return [...talent].sort((tal1, tal2) => {
        if (sortBy === "all-listOrder-asc") {
            const order = orderedTalentIds(customList?.talent, customList?.talentOrder)
            const indexOf = (id: string) => {
                const index = order.indexOf(id)
                return index === -1 ? Number.MAX_SAFE_INTEGER : index
            }
            return indexOf(tal1.id) - indexOf(tal2.id)
        }
        if (sort.value.toLowerCase().includes("price")) {
            const key = sort.function as keyof Prices
            const a = tal1.localPrices?.[key] || 0
            const b = tal2.localPrices?.[key] || 0
            return sort.direction === "desc" ? b - a : a - b
        }
        if (sortBy.toLowerCase().includes("followers")) {
            const a = followersForSort(tal1, sort.platform)
            const b = followersForSort(tal2, sort.platform)
            return sort.direction === "desc" ? b - a : a - b
        }
        if (sortBy === "tt-views-desc") {
            return (tal2.platformData?.TikTok?.stats?.averageViews || 0) - (tal1.platformData?.TikTok?.stats?.averageViews || 0)
        }
        if (sortBy === "tt-views-asc") {
            return (tal1.platformData?.TikTok?.stats?.averageViews || 0) - (tal2.platformData?.TikTok?.stats?.averageViews || 0)
        }
        if (sortBy === "tt-engagement-desc") {
            return (tal2.platformData?.TikTok?.stats?.engagementRate || 0) - (tal1.platformData?.TikTok?.stats?.engagementRate || 0)
        }
        if (sortBy === "tt-engagement-asc") {
            return (tal1.platformData?.TikTok?.stats?.engagementRate || 0) - (tal2.platformData?.TikTok?.stats?.engagementRate || 0)
        }
        if (sortBy === "all-tagRelevance-desc") {
            return tagRelevanceScore(tal2.contentTags, contentTags) - tagRelevanceScore(tal1.contentTags, contentTags)
        }
        if (sortBy === "all-tagRelevance-asc") {
            return tagRelevanceScore(tal1.contentTags, contentTags) - tagRelevanceScore(tal2.contentTags, contentTags)
        }
        if (sortBy === "all-selectedCreators-desc") {
            return Number(selectedIds.has(tal2.id)) - Number(selectedIds.has(tal1.id))
        }
        return 0
    })
}

function followersForSort(talent: Talent, platform: SortOption["platform"]) {
    if (platform === "tt") return talent.platformData?.TikTok?.followers || 0
    if (platform === "ig") return talent.platformData?.Instagram?.followers || 0
    return talent.platformData?.YouTube?.followers || 0
}

function tagRelevanceScore(talTags: string[] | undefined, contentTags: string[]) {
    const contentTagsLower = contentTags.map((tag) => tag.toLowerCase())
    const talTagsArr = [...(talTags || [])]
    talTagsArr.length = 5
    talTagsArr.reverse()
    let points = 0
    for (const [idx, tag] of talTagsArr.entries()) {
        if (contentTags.includes(tag || "") || contentTagsLower.includes((tag || "").toLowerCase())) {
            points += idx + 1
        }
    }
    return points
}
